/**
 * The travel runtime's ECS glue (plan R17): the player's nose, detached turning at the cruise rate scale, copying the
 * float64 kinematics to and from `Pos64`/`Vel`, the large ships that mass-lock the drive and the hostile weapon lock.
 * Per tick and allocation-free (D-014).
 */
import { SIM_DT } from '../../core/loop'
import type { Sim } from '../../sim'
import type { ShipBand } from '../../data/schemas'
import { Control, Life, Lock, Pos64, Rot, Ship, Team, Vel } from '../../world/components'
import { isHostile } from '../../combat/hostility'
import { integrateDetached, setMassLockShip } from '../math'
import type { TravelRuntime } from './state'

/** Writes the nose of attitude `rt.rot` (local −Z in the system frame) into `rt.nose`. */
function noseOfRot(rt: TravelRuntime): void {
  const q = rt.rot
  const n = rt.nose
  n.x = -2 * (q.x * q.z + q.w * q.y)
  n.y = -2 * (q.y * q.z - q.w * q.x)
  n.z = -(1 - 2 * (q.x * q.x + q.y * q.y))
}

/** Reads the ship's attitude into `rt.rot` and its nose (local −Z in the system frame) into `rt.nose`. */
export function readNose(rt: TravelRuntime, eid: number): void {
  const q = rt.rot
  q.x = Rot.x[eid]
  q.y = Rot.y[eid]
  q.z = Rot.z[eid]
  q.w = Rot.w[eid]
  noseOfRot(rt)
}

/**
 * Turns a detached ship for one tick by its own `Control` pitch, yaw and roll at `cruise.rateScale` × its ship def's
 * maximum rates (the same axes as the flight model), writes `Rot` and refreshes `rt.nose`. With no turn input the
 * attitude is left as it is.
 */
export function turnDetached(rt: TravelRuntime, sim: Sim, eid: number): void {
  readNose(rt, eid)
  if (Control.pitch[eid] === 0 && Control.yaw[eid] === 0 && Control.roll[eid] === 0) return
  const rate = sim.defs[Ship.defIndex[eid]].maxRate
  const q = rt.rot
  integrateDetached(q, Control.pitch[eid], Control.yaw[eid], Control.roll[eid], rate.pitch, rate.yaw, rate.roll, rt.configs.travel.cruise.rateScale, SIM_DT)
  Rot.x[eid] = q.x
  Rot.y[eid] = q.y
  Rot.z[eid] = q.z
  Rot.w[eid] = q.w
  noseOfRot(rt)
}

/** Loads the ship's float64 position into `rt.ship` (the ECS is the authority between ticks). */
/** The band of the hull `eid` flies (its def's `sizeBand`); null with no def. Allocation-free (M10 P5a7a). */
export function bandOfShip(sim: Pick<Sim, 'defs'>, eid: number): ShipBand | null {
  return sim.defs[Ship.defIndex[eid]]?.sizeBand ?? null
}

export function shipFromEcs(rt: TravelRuntime, eid: number): void {
  rt.ship.x = Pos64.x[eid]
  rt.ship.y = Pos64.y[eid]
  rt.ship.z = Pos64.z[eid]
}

/** Writes `rt.ship`'s position (float64) and velocity back to the ECS. */
export function shipToEcs(rt: TravelRuntime, eid: number): void {
  Pos64.x[eid] = rt.ship.x
  Pos64.y[eid] = rt.ship.y
  Pos64.z[eid] = rt.ship.z
  Vel.x[eid] = rt.ship.vx
  Vel.y[eid] = rt.ship.vy
  Vel.z[eid] = rt.ship.vz
}

/** The ship's speed (m/s) from its `Vel`. */
export function speedOf(eid: number): number {
  const vx = Vel.x[eid]
  const vy = Vel.y[eid]
  const vz = Vel.z[eid]
  return Math.sqrt(vx * vx + vy * vy + vz * vz)
}

/** Refreshes the mass-lock input's ship slots with every living large ship other than `eid` (up to the capacity). */
export function fillMassLockShips(rt: TravelRuntime, sim: Sim, eid: number): void {
  const input = rt.massLock
  if (input === null) return
  const minMass = rt.configs.runtime.largeShipMassKg
  const ships = sim.ships.dense
  let n = 0
  for (let i = 0; i < ships.length && n < input.shipCapacity; i++) {
    const e = ships[i]
    if (e === eid || Life.alive[e] !== 1) continue
    if (sim.defs[Ship.defIndex[e]].mass < minMass) continue
    setMassLockShip(input, n, e, Pos64.x[e], Pos64.y[e], Pos64.z[e])
    n++
  }
  input.shipCount = n
}

/** True when a living ship of a hostile team (`isHostile`) holds a torpedo lock on `eid`. */
export function hostileLockOn(sim: Sim, eid: number): boolean {
  const ships = sim.ships.dense
  const team = Team.id[eid]
  for (let i = 0; i < ships.length; i++) {
    const e = ships[i]
    if (e === eid || Life.alive[e] !== 1 || !isHostile(Team.id[e], team)) continue
    if (Lock.locked[e] === 1 && Lock.target[e] === eid) return true
  }
  return false
}
